import React, { type ReactNode, useContext } from 'react';
import type ApolloClient from 'apollo-client';
import {
	ConfluenceExtension,
	JiraExtension,
	type Dispatch,
	type ForgeDoc,
	type ForgeExtensionPoints,
	type RenderFn,
} from '@atlassian/forge-ui-types';
import cloneDeep from 'lodash/cloneDeep';
import { fg } from '@atlaskit/platform-feature-flags';

import { Text } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import { type ComponentMap, type ModalExtension } from './util';
import { getADSModalWidth } from '../utils';
import { useSendNodeCountAnalytics } from '../utils/useSendNodeCountAnalytics';
import { RendererContext } from '../context';
import { type FormProps } from '../components/UIKit';
import SectionMessage from '@atlaskit/section-message';
import warnOnce from '../utils/warn-once';
import { type Extension } from '../web-client';
import { ForgeExtensionPointModal } from './ForgeExtensionPointModal';
import { ExperimentalSkeletonOverlayWrapper } from '../components/skeleton-overlay';
import { shouldWrapComponent } from '../test-utils/RenderForgeDocFromJSX';
import { useIntl } from 'react-intl-next';

// Component prop deprecation warnings for the Typography Uplift. Remove after 1st of December 2025
export const sendComponentDeprecationWarnings = (
	componentType: string,
	forgeEnvironment: Extension['environmentType'],
): void => {
	if (forgeEnvironment === 'PRODUCTION') {
		return;
	}

	if (componentType === 'Heading') {
		warnOnce(
			`[@forge/react: Heading] Warning: The functionality of the "as" prop is being updated and should no longer be used to determine heading sizes. To avoid breaking changes, migrate to using the "size" prop. For more details, visit: https://atlassian.design/components/heading`,
		);
	}
};

export const getComponent = (componentMap: ComponentMap, componentType: string): RenderFn => {
	if (componentType === 'User' || componentType === 'Avatar') {
		return componentMap['User'] || componentMap['Avatar'];
	}
	if (componentType === 'UserGroup' || componentType === 'AvatarStack') {
		return componentMap['UserGroup'] || componentMap['AvatarStack'];
	}
	return componentMap[componentType];
};

const isAlignFormButtonsEnd = (extensionType: string, forgeDoc: ForgeDoc) => {
	// Confluence Byline and ContextMenu Forge apps with a Form component as
	// there only child shifts the form buttons to the end in the footer
	return (
		(extensionType === ConfluenceExtension.BYLINE ||
			extensionType === ConfluenceExtension.CONTEXT_MENU) &&
		forgeDoc.children[0]?.type === 'Form'
	);
};

export interface RendererNextProps {
	/** Apollo client to use for all product GraphQL queries */
	client?: ApolloClient<any>; // to be removed with forge_disable_fcf_modal_experience_on_issue_view fg cleanup
	/* Map of component types to render functions */
	components: ComponentMap;
	/* Error to throw when render function is not recognized */
	customRenderErrorFn?: (forgeDocType: string) => void;
	// defaultComponents: ComponentMap;
	/* Function used by components to dispatch effects */
	dispatch: Dispatch;
	/* Error message to show the user. Set when an unexpected client-side error happens. */
	error?: string;
	/* Contains all the details for the extension*/
	extension?: Extension; // to be removed with forge_disable_fcf_modal_experience_on_issue_view fg cleanup
	/* Contains the module type of the extension */
	extensionType?: ForgeExtensionPoints;
	/* ForgeDoc to be rendered */
	forgeDoc?: ForgeDoc;
	/* Indicates whether a CSUIKit app is being rendered*/
	isNative?: boolean;
	/* Replace the default spinner with a custom loading component. */
	loadingComponent?: React.ReactNode;
	/* Object that stores all necessary properties for a Modal Extension */
	modalExtension?: ModalExtension;
	/* Callback for a side effect to perform on success from the consuming app */
	onSuccess?: () => void;
	/* Experimental:Whether to wrap the rendered component with a skeleton overlay. */
	shouldWrapWithSkeletonOverlay?: boolean;
}

export type MemoizedComponentProps = {
	children: () => ReactNode;
	hasComponentUpdated: boolean;
};

const forgeDocWithModalComponent = (
	forgeDoc: ForgeDoc,
	modalExtension: ModalExtension,
): ForgeDoc => {
	const { closeModalExtension, title, modalWidth, onSubmitSuccess } = modalExtension;

	// ModalDialog component sets the default width to `medium`
	const akModalWidth = modalWidth ? getADSModalWidth(modalWidth) : undefined;

	const forgeDocWithModal = {
		...forgeDoc,
		children: [
			{
				type: 'ModalDialog',
				props: {
					onClose: closeModalExtension,
					header: title,
					width: akModalWidth,
					onSubmitSuccess: onSubmitSuccess,
				},
				children: forgeDoc.children,
			},
		],
	};

	return forgeDocWithModal;
};

const forgeDocWithFormEndButtons = (forgeDoc: ForgeDoc) => {
	if (forgeDoc.children.length === 0) {
		return forgeDoc;
	}

	return {
		...forgeDoc,
		children: [
			{
				...forgeDoc.children[0],
				props: { ...forgeDoc.children[0].props, alignFooterButtons: 'end' },
			},
		],
	};
};

export const RendererNext = ({
	forgeDoc,
	error,
	customRenderErrorFn,
	components,
	dispatch,
	modalExtension,
	extensionType,
	isNative,
	onSuccess,
	client,
	extension,
	shouldWrapWithSkeletonOverlay = false,
}: RendererNextProps): React.JSX.Element | null => {
	const { forgeEnvironment } = useContext(RendererContext);

	useSendNodeCountAnalytics({
		forgeDoc,
		forgeEnvironment: forgeEnvironment ?? 'DEVELOPMENT',
		UIKitVersion: isNative ? '2' : '1',
	});

	const intl = useIntl();

	const render = (forgeDoc: ForgeDoc) => {
		sendComponentDeprecationWarnings(forgeDoc.type, forgeEnvironment ?? 'DEVELOPMENT');

		// Restricts FilePicker and FileCard components to only be used in EAP
		if (
			(!fg('forge-ui-display-object-store-components') || forgeEnvironment === 'PRODUCTION') &&
			(forgeDoc.type === 'FilePicker' || forgeDoc.type === 'FileCard')
		) {
			return (
				<SectionMessage appearance="error">
					<Text>
						{intl.formatMessage({
							defaultMessage: 'FilePicker and FileCard components are in EAP. To start testing,',
						})}
					</Text>
					<Text> </Text>
					<Link
						href="https://ecosystem.atlassian.net/servicedesk/customer/portal/1040/group/3496/create/18937"
						target="_blank"
					>
						<Text>{intl.formatMessage({ defaultMessage: 'sign up here' })}</Text>
					</Link>
					<Text>.</Text>
				</SectionMessage>
			);
		}

		const renderFn = getComponent(components, forgeDoc.type);
		if (!renderFn) {
			if (customRenderErrorFn) {
				customRenderErrorFn(forgeDoc.type);
			} else {
				throw new Error(`Error rendering app - encountered unknown component ${forgeDoc.type}.`);
			}
		}

		onSuccess && onSuccess();

		if (shouldWrapWithSkeletonOverlay && shouldWrapComponent(forgeDoc.type)) {
			return (
				<ExperimentalSkeletonOverlayWrapper>
					{renderFn({ forgeDoc, dispatch, render })}
				</ExperimentalSkeletonOverlayWrapper>
			);
		}

		return renderFn({ forgeDoc, dispatch, render });
	};

	if (error) {
		return modalExtension ? (
			<ForgeExtensionPointModal modalExtension={modalExtension}>
				<SectionMessage title="Something went wrong" appearance="error">
					<Text>{error}</Text>
				</SectionMessage>
			</ForgeExtensionPointModal>
		) : (
			<SectionMessage title="Something went wrong" appearance="error">
				<Text>{error}</Text>
			</SectionMessage>
		);
	}

	if (!forgeDoc) {
		return null;
	}

	// Extensions rendered in a modal for version 10 and onwards are handled here.
	// This is the default behavior for handles model extensions across product modules.
	// When the modelExtension prop is present, and the forgeReactMajorVersion is 10 or higher,
	// the provided foreDoc is rendered within a Modal component with the provided modalExtension props.
	// This setup is to ensure that the modal extension is rendered correctly across all product modules,
	// and is the resolution for the HOT described in: https://ops.internal.atlassian.com/jira/browse/HOT-108146
	if (forgeDoc.forgeReactMajorVersion && forgeDoc.forgeReactMajorVersion >= 10 && modalExtension) {
		const isFirstChildAForm = forgeDoc.children[0]?.type === 'Form';
		const { onSubmitSuccess } = modalExtension;
		// injects onSubmitSuccess to into the Form component submit handler
		if (isFirstChildAForm) {
			// doing a deep clone here to make a copy of onSubmit
			const formPropsCopy = cloneDeep(forgeDoc.children[0].props) as FormProps;
			forgeDoc.children[0].props = {
				...formPropsCopy,
				onSubmit: async () => {
					const isSuccess = await formPropsCopy.onSubmit();
					if (onSubmitSuccess && (isSuccess === true || isSuccess === undefined)) {
						onSubmitSuccess();
					}
				},
			};
		}

		const shouldShowFCFModalDeprecationMessage =
			(extensionType === JiraExtension.CUSTOM_FIELD ||
				extensionType === JiraExtension.CUSTOM_FIELD_TYPE) &&
			!fg('forge_disable_fcf_modal_experience_on_issue_view');

		return (
			<ForgeExtensionPointModal
				modalExtension={modalExtension}
				shouldShowFCFModalDeprecationMessage={shouldShowFCFModalDeprecationMessage}
				client={client}
				extension={extension}
			>
				{render(forgeDoc)}
			</ForgeExtensionPointModal>
		);
	}

	const newForgeDoc =
		extensionType && isAlignFormButtonsEnd(extensionType, forgeDoc)
			? forgeDocWithFormEndButtons(forgeDoc)
			: forgeDoc;

	const extensionForgeDoc = modalExtension
		? forgeDocWithModalComponent(newForgeDoc, modalExtension)
		: newForgeDoc;

	return <>{render(extensionForgeDoc)}</>;
};
